import {
  type ScoutHost as ScoutHostGql,
  ScoutDataChangeStatus,
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import type { StreamStatus } from "../../../../stream";
import type { ScoutAttachment, ScoutOptimisticAttachmentPreview } from "../scoutSubmitAttachments";
import type { ScoutWriteModeGql } from "./scoutWriteMode";
import type {
  ScoutActiveTurn,
  ScoutChatMessage,
  ScoutDataChangeSummary,
  ScoutThreadSummary,
} from "./types";

import {
  respondedStatusForDataChange,
  type ScoutDataChangeCardStatus,
  type ScoutDataChangeProposalOperation,
} from "../Chat/scoutDataChangeProposal";
import { buildScoutPrivateDocumentExtractionWatchPartUrl } from "../Chat/scoutExtractionWatchMessage";
import { buildLargeFileFullAnalysisProposalId } from "../Chat/scoutLargeFileFullAnalysisProposal";
import { type ScoutConversationControllerSnapshot } from "./scoutConversationController";
import { isScoutOptimisticConversationId } from "./scoutConversationIds";
import { scoutConversationStore } from "./scoutConversationStore";
import {
  buildDataChangeProposalEventMessage,
  buildDataChangeStaleRetryMessage,
  staleRetryItemFromDataChange,
} from "./scoutDataChangeProposalEvents";
import {
  buildLargeFileFullAnalysisEventMessage,
  parseRespondedLargeFileFullAnalysisFromMessages,
  type LargeFileFullAnalysisEventStatus,
} from "./scoutLargeFileFullAnalysisEvents";
import {
  readDismissedScoutProUpsells,
  writeDismissedScoutProUpsells,
} from "./scoutProUpsellDismissalStorage";
import {
  buildTenantDocumentProposalEventMessage,
  parseRespondedTenantDocumentProposalsFromMessages,
  type TenantDocumentProposalEventStatus,
} from "./scoutTenantDocumentProposalEvents";
import { useScoutConversationController } from "./useScoutConversationController";
import {
  type ScoutConversationPrivateDocumentRow,
  useScoutConversationPrivateDocuments,
} from "./useScoutConversationPrivateDocuments";
import {
  useScoutDataChangeMutations,
  type ScoutDataChangeMutationResult,
} from "./useScoutDataChangeMutations";

export { SCOUT_INTERNAL_EVENT_PREFIX } from "./scoutInternalEvents";

type UseScoutConversationOptions = {
  tenantId: string | undefined;
  conversationId: string | undefined;
  onConversationIdChange?: (conversationId: string) => void;
  enablePrivateDocuments?: boolean;
};

type SendScoutMessageOptions = {
  /** `null` forces a brand new conversation; omit to use the displayed one. */
  conversationId?: string | null;
  writeMode?: ScoutWriteModeGql;
  pageContext?: string;
  /** Which UI surface initiated the send. Threaded to backend PostHog events. */
  host?: ScoutHostGql;
  attachmentIds?: string[];
  extractionWatchPrivateDocumentIds?: string[];
  attachmentPreviews?: ScoutOptimisticAttachmentPreview[];
};

type UseScoutConversationResult = {
  conversation: ScoutThreadSummary | undefined;
  conversationId: string | undefined;
  conversations: ScoutThreadSummary[];
  messages: ScoutChatMessage[];
  activeTurn: ScoutActiveTurn | undefined;
  loading: boolean;
  error: Error | undefined;
  streamStatus: StreamStatus | undefined;
  isSending: boolean;
  isAborting: boolean;
  isLoadingOlderMessages: boolean;
  hasOlderMessages: boolean;
  sendMessage: (content: string, options?: SendScoutMessageOptions) => Promise<string | undefined>;
  abortTurn: () => Promise<boolean>;
  refetch: () => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  largeDocumentJob: ScoutConversationPrivateDocumentRow | undefined;
  largeDocumentOptIns: ScoutConversationPrivateDocumentRow[];
  privateDocuments: ScoutConversationPrivateDocumentRow[];
  dismissLargeDocumentOptIn: (privateDocumentIds: string[]) => Promise<void>;
  confirmLargeDocumentAnalysis: (
    input:
      | {
          privateDocumentIds: string[];
          documents: ScoutConversationPrivateDocumentRow[];
        }
      | string
  ) => Promise<void>;
  isConfirmingLargeDocumentAnalysis: boolean;
  isDecliningLargeDocumentAnalysis: boolean;
  registerComposerAttachments: (attachments: ScoutAttachment[]) => void;
  unregisterComposerAttachment: (privateDocumentId: string) => Promise<void>;
  respondedTenantDocumentProposals: Record<string, "confirmed" | "declined">;
  isProcessingTenantDocumentProposal: boolean;
  confirmTenantDocumentProposal: (input: {
    proposalId: string;
    documentIds: string[];
    totalProposedCount: number;
  }) => Promise<void>;
  declineTenantDocumentProposal: (input: {
    proposalId: string;
    totalProposedCount: number;
  }) => Promise<void>;
  respondedLargeFileFullAnalysisProposals: Record<string, "confirmed" | "declined">;
  confirmLargeFileFullAnalysisProposal: (input: {
    proposalId: string;
    privateDocumentIds: string[];
    documents: ScoutConversationPrivateDocumentRow[];
  }) => Promise<void>;
  declineLargeFileFullAnalysisProposal: (input: {
    proposalId: string;
    privateDocumentIds: string[];
  }) => Promise<void>;
  respondedDataChangeProposals: Record<string, ScoutDataChangeCardStatus>;
  isProcessingDataChangeProposal: boolean;
  resolveDataChangeProposals: (input: {
    proposals: Array<{
      proposalId: string;
      title: string;
      status: "confirmed" | "declined";
      operation?: ScoutDataChangeProposalOperation;
    }>;
  }) => Promise<void>;
  dismissedScoutProUpsells: Record<string, "dismissed">;
  dismissScoutProUpsell: (input: { upsellId: string }) => Promise<void>;
  /** Reverse applied changes. Failure is a result field, not a thrown mutation. */
  undoDataChange: (changeIds: string[]) => Promise<ScoutDataChangeMutationResult[]>;
  /** Ask Scout to propose compensating changes for stale undo failures. */
  retryStaleDataChangeUndo: (changeIds: string[]) => Promise<void>;
};

const EMPTY_CONTROLLER_SNAPSHOT: ScoutConversationControllerSnapshot = {
  storeSnapshot: {
    conversationsById: {},
    messagesByConversationId: {},
    activeTurnsByConversationId: {},
    streamStatusByConversationId: {},
  },
  runtimesByConversationId: {},
  reconciledConversationIdsByConversationId: {},
};

const EMPTY_RUNTIME_SNAPSHOT = {
  loading: false,
  error: undefined,
  isSending: false,
  isAborting: false,
  isLoadingOlderMessages: false,
  hasOlderMessages: false,
};

function dataChangeSummariesFromUndoMutation(
  results: ScoutDataChangeMutationResult[]
): ScoutDataChangeSummary[] {
  const summaries: ScoutDataChangeSummary[] = [];
  for (const result of results) {
    const terminal = result.status !== ScoutDataChangeStatus.Applied;
    const summary: ScoutDataChangeSummary = {
      id: result.changeId,
      status: result.status,
      undoable: !terminal,
    };
    if (terminal) {
      summary.expiresAt = null;
    }
    summaries.push(summary);
  }
  return summaries;
}

/**
 * React-facing adapter over the shared Scout controller + store.
 *
 * The hook owns only view selection concerns:
 * - which conversation this consumer is currently displaying
 * - when to attach/detach that displayed conversation from the shared controller
 *
 * The hook does not own Scout's runtime state machine. History loading, SSE,
 * optimistic reconciliation, and abort handling all live in the controller.
 */
export function useScoutConversation({
  tenantId,
  conversationId,
  onConversationIdChange,
  enablePrivateDocuments = true,
}: UseScoutConversationOptions): UseScoutConversationResult {
  const controller = useScoutConversationController();
  const controllerSnapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    () => EMPTY_CONTROLLER_SNAPSHOT
  );

  const storeSnapshot = controllerSnapshot.storeSnapshot;
  const [displayConversationId, setDisplayConversationId] = useState(conversationId);

  // Route changes still drive the local display target for this hook instance.
  useEffect(() => {
    setDisplayConversationId(conversationId);
  }, [conversationId]);

  // Attaches just the conversation this hook is currently rendering to the
  // shared controller. Other hook instances can attach other conversations at
  // the same time.
  useEffect(() => {
    if (!tenantId || !displayConversationId) {
      return;
    }

    controller.attachConversation({
      tenantId,
      conversationId: displayConversationId,
    });

    return () => {
      controller.detachConversation(displayConversationId);
    };
  }, [controller, displayConversationId, tenantId]);

  // During first-send optimistic startup, the controller may have already moved
  // state from the optimistic id to the persisted id before this hook has
  // updated its local display id. This alias keeps rendering stable through that
  // handoff.
  const effectiveConversationId =
    (displayConversationId
      ? controllerSnapshot.reconciledConversationIdsByConversationId[displayConversationId]
      : undefined) ?? displayConversationId;

  const conversations = Object.values(storeSnapshot.conversationsById)
    .filter((conversation) => !tenantId || conversation.tenantId === tenantId)
    .sort((first, second) => (second.updatedAt ?? "").localeCompare(first.updatedAt ?? ""));
  const conversation = effectiveConversationId
    ? storeSnapshot.conversationsById[effectiveConversationId]
    : undefined;
  const messages = effectiveConversationId
    ? (storeSnapshot.messagesByConversationId[effectiveConversationId] ?? [])
    : [];
  const activeTurn = effectiveConversationId
    ? storeSnapshot.activeTurnsByConversationId[effectiveConversationId]
    : undefined;
  const streamStatus = effectiveConversationId
    ? storeSnapshot.streamStatusByConversationId[effectiveConversationId]
    : undefined;
  const runtime = effectiveConversationId
    ? (controllerSnapshot.runtimesByConversationId[effectiveConversationId] ??
      EMPTY_RUNTIME_SNAPSHOT)
    : EMPTY_RUNTIME_SNAPSHOT;

  // const persistedConversationId = resolvePersistedScoutConversationId(
  //   effectiveConversationId,
  //   controllerSnapshot.reconciledConversationIdsByConversationId
  // );

  const {
    documents: privateDocuments,
    largeDocumentJob,
    largeDocumentOptIns,
    dismissLargeDocumentOptIn,
    confirmFullAnalysis,
    isConfirming: isConfirmingLargeDocumentAnalysis,
    isDeclining: isDecliningLargeDocumentAnalysis,
    refreshPrivateDocuments,
    trackLargeDocumentAttachments,
    removeTrackedLargeDocumentAttachments,
    ingestTenantDocuments,
    isIngestingTenantDocuments,
  } = useScoutConversationPrivateDocuments({
    tenantId,
    conversationId: effectiveConversationId,
    enabled: enablePrivateDocuments,
  });

  const {
    confirmDataChange,
    declineDataChange,
    undoDataChange: undoDataChangeMutation,
  } = useScoutDataChangeMutations();

  const [respondedTenantDocumentProposals, setRespondedTenantDocumentProposals] = useState<
    Record<string, "confirmed" | "declined">
  >({});
  const [respondedLargeFileFullAnalysisProposals, setRespondedLargeFileFullAnalysisProposals] =
    useState<Record<string, "confirmed" | "declined">>({});
  const [respondedDataChangeProposals, setRespondedDataChangeProposals] = useState<
    Record<string, ScoutDataChangeCardStatus>
  >({});
  const [isProcessingDataChangeProposal, setIsProcessingDataChangeProposal] = useState(false);

  // Dismissed-upsell state is client-only on purpose: clicking dismiss must
  // not trigger a Scout turn. localStorage keyed on conversationId is the
  // durable store; this React state mirrors it for synchronous reads.
  const [dismissedScoutProUpsellsState, setDismissedScoutProUpsellsState] = useState<
    Record<string, "dismissed">
  >(() => readDismissedScoutProUpsells(effectiveConversationId));

  // When the displayed conversation changes (route switch, optimistic→
  // persisted id reconciliation), reload the dismissed-upsell state from
  // storage for the new conversation.
  useEffect(() => {
    setDismissedScoutProUpsellsState(readDismissedScoutProUpsells(effectiveConversationId));
  }, [effectiveConversationId]);

  const sendMessage = useCallback(
    async (content: string, options?: SendScoutMessageOptions) => {
      if (!tenantId) {
        throw new Error("Scout requires an organization before sending a message.");
      }

      // `effectiveConversationId` trails the prop by a render (it is mirrored
      // through state), so a caller that just cleared the conversation cannot
      // rely on the fallback — it passes `null` to say "start fresh" instead.
      const targetConversationId =
        options?.conversationId === null
          ? undefined
          : (options?.conversationId ?? effectiveConversationId);

      if (enablePrivateDocuments && options?.attachmentPreviews?.length) {
        trackLargeDocumentAttachments(
          options.attachmentPreviews.map((preview) => ({
            privateDocumentId: preview.privateDocumentId,
            filename: preview.filename,
            sizeBytes: preview.sizeBytes,
            ingestionMode: preview.ingestionMode,
            extractionStatus: preview.extractionStatus,
          }))
        );
      }

      const { optimisticConversationId, completion } = controller.sendMessage({
        tenantId,
        conversationId: targetConversationId,
        content,
        writeMode: options?.writeMode,
        ...(options?.pageContext ? { pageContext: options.pageContext } : {}),
        ...(options?.host ? { host: options.host } : {}),
        attachmentIds: enablePrivateDocuments ? options?.attachmentIds : undefined,
        attachmentPreviews: enablePrivateDocuments ? options?.attachmentPreviews : undefined,
        extractionWatchPrivateDocumentIds: enablePrivateDocuments
          ? options?.extractionWatchPrivateDocumentIds
          : undefined,
      });

      if (targetConversationId && !isScoutOptimisticConversationId(targetConversationId)) {
        void refreshPrivateDocuments(targetConversationId);
      }

      // For a brand new conversation, switch the UI immediately to the
      // optimistic conversation id so the user sees their message and the local
      // Thinking state without waiting for navigation. Propagate the optimistic
      // id to other hook instances too (e.g. the minimized footer launcher)
      // so in-flight affordances latch as soon as the user minimizes.
      if (!targetConversationId) {
        setDisplayConversationId(optimisticConversationId);
        onConversationIdChange?.(optimisticConversationId);
      }

      const resolvedConversationId = await completion;
      if (resolvedConversationId && resolvedConversationId !== targetConversationId) {
        setDisplayConversationId(resolvedConversationId);
        onConversationIdChange?.(resolvedConversationId);
      }

      await refreshPrivateDocuments(resolvedConversationId ?? targetConversationId ?? undefined);

      return resolvedConversationId;
    },
    [
      controller,
      enablePrivateDocuments,
      effectiveConversationId,
      onConversationIdChange,
      refreshPrivateDocuments,
      tenantId,
      trackLargeDocumentAttachments,
    ]
  );

  const abortTurn = useCallback(async () => {
    if (!tenantId || !effectiveConversationId) {
      return false;
    }

    return controller.abortTurn({
      tenantId,
      conversationId: effectiveConversationId,
    });
  }, [controller, effectiveConversationId, tenantId]);

  const refetch = useCallback(async () => {
    if (!tenantId || !displayConversationId) {
      return;
    }

    await controller.refetchConversation({
      tenantId,
      conversationId: displayConversationId,
    });
  }, [controller, displayConversationId, tenantId]);

  const confirmLargeFileFullAnalysisProposal = useCallback(
    async (input: {
      proposalId: string;
      privateDocumentIds: string[];
      documents: ScoutConversationPrivateDocumentRow[];
    }) => {
      const privateDocumentIds = Array.from(new Set(input.privateDocumentIds));
      if (privateDocumentIds.length === 0) {
        return;
      }

      const proposalId =
        input.proposalId.trim().length > 0
          ? input.proposalId
          : buildLargeFileFullAnalysisProposalId(privateDocumentIds);

      const documentsById = new Map(input.documents.map((document) => [document.id, document]));

      await confirmFullAnalysis(privateDocumentIds);

      const synthMessage = buildLargeFileFullAnalysisEventMessage({
        status: "confirmed",
        proposalId,
        count: privateDocumentIds.length,
        privateDocumentIds,
      });

      await sendMessage(synthMessage, {
        writeMode: conversation?.writeMode,
        attachmentIds: privateDocumentIds,
        attachmentPreviews: privateDocumentIds.map((privateDocumentId) => {
          const document = documentsById.get(privateDocumentId);
          return {
            privateDocumentId,
            filename: document?.filename ?? "Document.pdf",
            mediaType: "application/pdf",
            sizeBytes: document?.sizeBytes ?? 0,
            ingestionMode:
              document?.ingestionMode ?? ScoutPrivateDocumentIngestionMode.LargeFileExtraction,
            extractionStatus: ScoutPrivateDocumentExtractionStatus.Pending,
            filePartUrl: buildScoutPrivateDocumentExtractionWatchPartUrl(privateDocumentId),
          };
        }),
        extractionWatchPrivateDocumentIds: privateDocumentIds,
      });

      setRespondedLargeFileFullAnalysisProposals((current) => ({
        ...current,
        [proposalId]: "confirmed",
      }));
    },
    [confirmFullAnalysis, conversation?.writeMode, sendMessage]
  );

  /**
   * ChatFooter / OptInButtons entry point. Computes a stable batch proposalId
   * and delegates to the shared large-file resolve handler (mutation +
   * synthetic continue event).
   */
  const confirmLargeDocumentAnalysis = useCallback(
    async (
      input:
        | {
            privateDocumentIds: string[];
            documents: ScoutConversationPrivateDocumentRow[];
          }
        | string
    ) => {
      const privateDocumentIds =
        typeof input === "string" ? [input] : Array.from(new Set(input.privateDocumentIds));
      if (privateDocumentIds.length === 0) {
        return;
      }

      await confirmLargeFileFullAnalysisProposal({
        proposalId: buildLargeFileFullAnalysisProposalId(privateDocumentIds),
        privateDocumentIds,
        documents: typeof input === "string" ? [] : input.documents,
      });
    },
    [confirmLargeFileFullAnalysisProposal]
  );

  const declineLargeFileFullAnalysisProposal = useCallback(
    async (input: { proposalId: string; privateDocumentIds: string[] }) => {
      const privateDocumentIds = Array.from(new Set(input.privateDocumentIds));
      if (privateDocumentIds.length === 0) {
        return;
      }

      const proposalId =
        input.proposalId.trim().length > 0
          ? input.proposalId
          : buildLargeFileFullAnalysisProposalId(privateDocumentIds);

      await dismissLargeDocumentOptIn(privateDocumentIds);

      const synthMessage = buildLargeFileFullAnalysisEventMessage({
        status: "declined",
        proposalId,
        count: privateDocumentIds.length,
      });
      await sendMessage(synthMessage, { writeMode: conversation?.writeMode });

      setRespondedLargeFileFullAnalysisProposals((current) => ({
        ...current,
        [proposalId]: "declined",
      }));
    },
    [conversation?.writeMode, dismissLargeDocumentOptIn, sendMessage]
  );

  /**
   * ChatFooter Decline entry point — wraps the GraphQL decline mutation with
   * a synthetic continue event so Scout gets an explicit next-turn signal.
   */
  const dismissLargeDocumentOptInWithContinue = useCallback(
    async (privateDocumentIds: string[]) => {
      const uniqueIds = Array.from(new Set(privateDocumentIds));
      if (uniqueIds.length === 0) {
        return;
      }
      await declineLargeFileFullAnalysisProposal({
        proposalId: buildLargeFileFullAnalysisProposalId(uniqueIds),
        privateDocumentIds: uniqueIds,
      });
    },
    [declineLargeFileFullAnalysisProposal]
  );

  const confirmTenantDocumentProposal = useCallback(
    async (input: { proposalId: string; documentIds: string[]; totalProposedCount: number }) => {
      const uniqueDocumentIds = [...new Set(input.documentIds)];
      if (uniqueDocumentIds.length === 0) {
        return;
      }

      const { privateDocumentIds, privateDocuments } =
        await ingestTenantDocuments(uniqueDocumentIds);

      const synthMessage = buildTenantDocumentProposalEventMessage({
        status: "confirmed",
        proposalId: input.proposalId,
        count: privateDocumentIds.length,
        totalProposedCount: input.totalProposedCount,
      });

      // Surface extraction-watch attachments for every newly-created
      // PrivateDocument so the existing inline progress card renders in the
      // chat (matches the direct upload flow — Meredith's request). Cards
      // for non-large-PDF docs render as no-ops via the card's visibility
      // check, so it's safe to include them all without filtering by mode.
      const watchIds = privateDocumentIds.length > 0 ? privateDocumentIds : undefined;
      const documentById = new Map(privateDocuments.map((document) => [document.id, document]));
      const attachmentPreviews = watchIds?.map((privateDocumentId) => {
        const document = documentById.get(privateDocumentId);
        return {
          privateDocumentId,
          filename: document?.filename ?? "document",
          mediaType: document?.mediaType ?? "application/octet-stream",
          sizeBytes: document?.sizeBytes ?? 0,
          ingestionMode:
            document?.ingestionMode ?? ScoutPrivateDocumentIngestionMode.LargeFileExtraction,
          extractionStatus:
            document?.extractionStatus ?? ScoutPrivateDocumentExtractionStatus.Pending,
          filePartUrl: buildScoutPrivateDocumentExtractionWatchPartUrl(privateDocumentId),
        };
      });

      await sendMessage(synthMessage, {
        writeMode: conversation?.writeMode,
        attachmentIds: watchIds,
        extractionWatchPrivateDocumentIds: watchIds,
        attachmentPreviews,
      });

      setRespondedTenantDocumentProposals((current) => ({
        ...current,
        [input.proposalId]: "confirmed",
      }));
    },
    [conversation?.writeMode, ingestTenantDocuments, sendMessage]
  );

  const declineTenantDocumentProposal = useCallback(
    async (input: { proposalId: string; totalProposedCount: number }) => {
      const synthMessage = buildTenantDocumentProposalEventMessage({
        status: "declined",
        proposalId: input.proposalId,
        count: input.totalProposedCount,
        totalProposedCount: input.totalProposedCount,
      });

      await sendMessage(synthMessage, { writeMode: conversation?.writeMode });

      setRespondedTenantDocumentProposals((current) => ({
        ...current,
        [input.proposalId]: "declined",
      }));
    },
    [conversation?.writeMode, sendMessage]
  );

  /**
   * Resolve staged ask-mode mutations in one Scout turn (Accept all, Reject
   * all, or a fully-decided multi-card stack). Sends a single synthetic
   * continue event with one line per proposalId. Does not apply GraphQL —
   * the agent must call execute_graphql with approvedProposalId after Accept.
   */
  const resolveDataChangeProposals = useCallback(
    async (input: {
      proposals: Array<{
        proposalId: string;
        title: string;
        status: "confirmed" | "declined";
        operation?: ScoutDataChangeProposalOperation;
      }>;
    }) => {
      if (input.proposals.length === 0) return;
      const proposalIds = input.proposals.map((proposal) => proposal.proposalId);
      setIsProcessingDataChangeProposal(true);
      try {
        // Set apply-authority on the durable ScoutDataChange rows FIRST. The
        // row status (CONFIRMED / DECLINED) — not the synth message below — is
        // what the follow-up execute_graphql apply reads, so this must land
        // before the model is nudged to apply. Requires a tenant + persisted
        // conversation; both are present whenever a card is on screen.
        if (tenantId && effectiveConversationId) {
          await Promise.all(
            input.proposals.map((proposal) =>
              proposal.status === "confirmed"
                ? confirmDataChange({ changeId: proposal.proposalId })
                : declineDataChange({ changeId: proposal.proposalId })
            )
          );
        }

        const synthMessage = buildDataChangeProposalEventMessage({
          proposals: input.proposals.map((proposal) => ({
            proposalId: proposal.proposalId,
            title: proposal.title,
            status: proposal.status,
          })),
        });
        await sendMessage(synthMessage, { writeMode: conversation?.writeMode });
        setRespondedDataChangeProposals((current) => {
          const next = { ...current };
          for (const proposal of input.proposals) {
            next[proposal.proposalId] = proposal.status;
          }
          return next;
        });
      } catch (error) {
        // If the continue send fails, reopen cards so the user can retry.
        setRespondedDataChangeProposals((current) => {
          const next = { ...current };
          for (const proposalId of proposalIds) {
            delete next[proposalId];
          }
          return next;
        });
        throw error;
      } finally {
        setIsProcessingDataChangeProposal(false);
      }
    },
    [
      conversation?.writeMode,
      sendMessage,
      tenantId,
      effectiveConversationId,
      confirmDataChange,
      declineDataChange,
    ]
  );

  const dismissScoutProUpsell = useCallback(
    async (input: { upsellId: string }) => {
      // Client-only: hide the card and persist to localStorage. Do NOT send a
      // synthetic message — that would trigger a Scout turn and an unwanted
      // acknowledgement reply. The model's "call the tool at most once per
      // conversation" rule (via its own tool-call history) keeps it from
      // re-pitching, so a server-visible signal isn't needed.
      setDismissedScoutProUpsellsState((current) => {
        if (current[input.upsellId] === "dismissed") return current;
        const next = { ...current, [input.upsellId]: "dismissed" as const };
        writeDismissedScoutProUpsells(effectiveConversationId, next);
        return next;
      });
    },
    [effectiveConversationId]
  );

  const registerComposerAttachments = useCallback(
    (attachments: ScoutAttachment[]) => {
      trackLargeDocumentAttachments(
        attachments.map((attachment) => ({
          privateDocumentId: attachment.privateDocumentId,
          filename: attachment.filename,
          sizeBytes: attachment.sizeBytes,
          ingestionMode: attachment.ingestionMode,
          extractionStatus: attachment.extractionStatus,
        }))
      );
      void refreshPrivateDocuments(effectiveConversationId ?? undefined);
    },
    [effectiveConversationId, refreshPrivateDocuments, trackLargeDocumentAttachments]
  );

  const unregisterComposerAttachment = useCallback(
    async (privateDocumentId: string) => {
      removeTrackedLargeDocumentAttachments([privateDocumentId]);
      await refreshPrivateDocuments(effectiveConversationId ?? undefined);
    },
    [effectiveConversationId, refreshPrivateDocuments, removeTrackedLargeDocumentAttachments]
  );

  const loadOlderMessages = useCallback(async () => {
    if (!tenantId || !displayConversationId) {
      return;
    }

    await controller.loadOlderMessages({
      tenantId,
      conversationId: displayConversationId,
    });
  }, [controller, displayConversationId, tenantId]);

  // Rehydrate "already responded" state from the persisted message history so
  // the proposal card doesn't re-prompt after a page refresh. In-session state
  // overrides history (covers the brief window between click and the synth
  // message round-tripping back into `messages`). Keyed on the underlying
  // store map rather than the local `messages` variable, which creates a new
  // empty array on each render when no conversation is active.
  const respondedProposalsFromHistory = useMemo<
    Record<string, TenantDocumentProposalEventStatus>
  >(() => {
    if (!effectiveConversationId) return {};
    const messagesForConversation =
      storeSnapshot.messagesByConversationId[effectiveConversationId] ?? [];
    return parseRespondedTenantDocumentProposalsFromMessages(messagesForConversation);
  }, [effectiveConversationId, storeSnapshot.messagesByConversationId]);

  const mergedRespondedTenantDocumentProposals = useMemo(
    () => ({
      ...respondedProposalsFromHistory,
      ...respondedTenantDocumentProposals,
    }),
    [respondedProposalsFromHistory, respondedTenantDocumentProposals]
  );

  const respondedLargeFileFromHistory = useMemo<
    Record<string, LargeFileFullAnalysisEventStatus>
  >(() => {
    if (!effectiveConversationId) return {};
    const messagesForConversation =
      storeSnapshot.messagesByConversationId[effectiveConversationId] ?? [];
    return parseRespondedLargeFileFullAnalysisFromMessages(messagesForConversation);
  }, [effectiveConversationId, storeSnapshot.messagesByConversationId]);

  const mergedRespondedLargeFileFullAnalysisProposals = useMemo(
    () => ({
      ...respondedLargeFileFromHistory,
      ...respondedLargeFileFullAnalysisProposals,
    }),
    [respondedLargeFileFromHistory, respondedLargeFileFullAnalysisProposals]
  );

  // Responded-state now comes from the durable ScoutDataChange row status
  // projected onto conversation history — not a scan of synthetic
  // `[scout-internal-event]` messages. In-session state covers the
  // click→round-trip window while the row is still AWAITING_CONFIRMATION;
  // once the row advances (including UNDONE), the durable status wins so
  // the card cannot reopen.
  const respondedDataChangeFromHistory = useMemo<Record<string, ScoutDataChangeCardStatus>>(() => {
    if (!effectiveConversationId) return {};
    const dataChanges = storeSnapshot.conversationsById[effectiveConversationId]?.dataChanges ?? [];
    const responded: Record<string, ScoutDataChangeCardStatus> = {};
    for (const change of dataChanges) {
      const status = respondedStatusForDataChange(change.status);
      if (status) {
        responded[change.id] = status;
      }
    }
    return responded;
  }, [effectiveConversationId, storeSnapshot.conversationsById]);

  const mergedRespondedDataChangeProposals = useMemo(
    () => ({
      ...respondedDataChangeProposals,
      ...respondedDataChangeFromHistory,
    }),
    [respondedDataChangeFromHistory, respondedDataChangeProposals]
  );

  const undoDataChange = useCallback(
    async (changeIds: string[]): Promise<ScoutDataChangeMutationResult[]> => {
      if (!tenantId || !effectiveConversationId || changeIds.length === 0) {
        return [];
      }
      // Reverse first (same as Accept/Reject). Failure is a populated
      // failureCategory / failureReason on the result, not a thrown mutation.
      const results = await undoDataChangeMutation({ changeIds });
      scoutConversationStore.patchConversationDataChanges(
        effectiveConversationId,
        dataChangeSummariesFromUndoMutation(results)
      );
      return results;
    },
    [tenantId, effectiveConversationId, undoDataChangeMutation]
  );

  const retryStaleDataChangeUndo = useCallback(
    async (changeIds: string[]) => {
      const byId = new Map((conversation?.dataChanges ?? []).map((row) => [row.id, row]));
      const items = changeIds.map((changeId) =>
        staleRetryItemFromDataChange(changeId, byId.get(changeId)?.diff)
      );
      const message = buildDataChangeStaleRetryMessage(items);
      if (!message) return;
      await sendMessage(message, { writeMode: conversation?.writeMode });
    },
    [conversation?.dataChanges, conversation?.writeMode, sendMessage]
  );

  return {
    conversation,
    conversationId: displayConversationId,
    conversations,
    messages,
    activeTurn,
    loading: runtime.loading,
    error: runtime.error,
    streamStatus,
    isSending: runtime.isSending,
    isAborting: runtime.isAborting,
    isLoadingOlderMessages: runtime.isLoadingOlderMessages,
    hasOlderMessages: runtime.hasOlderMessages,
    sendMessage,
    abortTurn,
    refetch,
    loadOlderMessages,
    largeDocumentJob,
    largeDocumentOptIns,
    privateDocuments,
    dismissLargeDocumentOptIn: dismissLargeDocumentOptInWithContinue,
    confirmLargeDocumentAnalysis,
    isConfirmingLargeDocumentAnalysis,
    isDecliningLargeDocumentAnalysis,
    registerComposerAttachments,
    unregisterComposerAttachment,
    respondedTenantDocumentProposals: mergedRespondedTenantDocumentProposals,
    isProcessingTenantDocumentProposal: isIngestingTenantDocuments,
    confirmTenantDocumentProposal,
    declineTenantDocumentProposal,
    respondedLargeFileFullAnalysisProposals: mergedRespondedLargeFileFullAnalysisProposals,
    confirmLargeFileFullAnalysisProposal,
    declineLargeFileFullAnalysisProposal,
    respondedDataChangeProposals: mergedRespondedDataChangeProposals,
    isProcessingDataChangeProposal,
    resolveDataChangeProposals,
    dismissedScoutProUpsells: dismissedScoutProUpsellsState,
    dismissScoutProUpsell,
    undoDataChange,
    retryStaleDataChangeUndo,
  };
}
